import FactoryLevel from 'frontend/helpers/factoryLevel'
import { store as baseStore } from 'frontend/store/store'
import { FALLBACK_LANGUAGE, getLanguage } from 'frontcore/i18n'
import { usePerimeterStore } from 'frontcore/store/usePerimeterStore'

import type { Pattern, Perimeter } from 'frontcore/presentation/perimeter-finder/domain/types'

type FactoryLevelStore = {
  commit: (mutation: 'setFactoryLevels', factoryLevels: FactoryLevel[]) => void
}
const store = baseStore as unknown as FactoryLevelStore

type CreateOptions = {
  filterAllowedPerimeters: boolean
}
const defaultOptions: CreateOptions = {
  filterAllowedPerimeters: true,
}

const useFactoryLevelBuilder = () => {
  const perimeterStore = usePerimeterStore()

  const createFactoryLevelStructure = ({ filterAllowedPerimeters }: CreateOptions = defaultOptions): void => {
    const language = getLanguage()
    const factoryLevelMap: Record<Pattern['id'], FactoryLevel> = {}
    const factoryLevels = perimeterStore.getPatterns.map((pattern: Pattern): FactoryLevel => {
      const patternName = pattern.localized[language] ?? pattern.localized[FALLBACK_LANGUAGE]
      const factoryLevel = new FactoryLevel(
        pattern.id,
        FactoryLevel.PATTERN,
        patternName,
        pattern.parent,
        pattern.callers,
        [],
        [],
        0
      )
      factoryLevelMap[pattern.id] = factoryLevel
      return factoryLevel
    })

    const perimeters = filterAllowedPerimeters ? perimeterStore.getAllowedPerimeters : perimeterStore.getAllPerimeters
    perimeters.forEach((perimeter: Perimeter): void => {
      const factoryLevel = factoryLevelMap[perimeter.pattern]
      if (factoryLevel === undefined) { return }
      let name = perimeter.name
      if (perimeter.description) {
        name += ` ${perimeter.description}`
      }
      factoryLevel.addItem({ id: perimeter.id, name, parent: perimeter.parent })
    })

    factoryLevels.forEach((factoryLevel: FactoryLevel): void => {
      const ids = factoryLevel.items.map((item) => item.id)
      if (factoryLevel.items.length !== 1) { return }
      factoryLevel.setSelectedItemIds(ids)
    })

    store.commit('setFactoryLevels', factoryLevels)
  }

  return { createFactoryLevelStructure }
}

export default useFactoryLevelBuilder
